"use client";

import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { useLocale } from "next-intl";

import { CONSENT_CHANGE_EVENT } from "@/features/consent/consent-storage";
import { appendLandingAttributionToHref } from "@/integrations/posthog/landing-attribution";
import type { AppLocale } from "@/i18n/routing";

import { useCartCount } from "./CartCountProvider";
import { getCartHref } from "./cart-href";
import { usePhoneUpsellGate } from "./phone-upsell-gate-context";

export interface CartNavigation {
  /** Locale-aware `/cart` path. */
  href: string;
  /**
   * Click handler for the anchor. Cancels the navigation while an
   * add-to-cart is in flight — see `parkCartNavigation` in
   * `CartCountProvider` for why, and note the anchor MUST stay a plain
   * `<a>` so the eventual load is a full document load.
   */
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  /** True once a tap has been parked and is waiting on the add. */
  isParked: boolean;
  /**
   * True from the moment a cart navigation begins — a click here, or a
   * parked click released by `endAdd` — until the document unloads. The
   * cart page is dynamic and per-user, so the full load takes a beat;
   * buttons render a pending state off this so the click visibly landed.
   */
  isNavigating: boolean;
}

/**
 * Wiring for every link that points at the cart. Centralized because all
 * of them (header desktop + mobile, the configurator's add-to-cart popup)
 * sit next to the add-to-cart CTA and can therefore abort an in-flight add.
 */
export function useCartNavigation(): CartNavigation {
  const locale = useLocale() as AppLocale;
  const {
    parkCartNavigation,
    isCartNavigationParked,
    isCartNavigationInFlight,
    beginCartNavigation,
  } = useCartCount();
  const { interceptCartNavigation } = usePhoneUpsellGate();
  const baseHref = getCartHref(locale);

  // The cart load is a full document load, which discards the in-memory
  // landing snapshot — in the one document that reads it at checkout. Carrying
  // it in the URL is the only way it survives (`appendLandingAttributionToHref`):
  // the reorder-QR marker always, the UTM quartet only for a visitor who has
  // not answered the banner yet. The parked-navigation release re-uses this
  // href, so it is covered too.
  //
  // Settled in an effect rather than computed during render, and that is not a
  // style choice: this hook renders on the server as well, where there is no
  // snapshot to read, and React does not patch a differing attribute onto
  // hydrated server markup — the header's links kept the bare SSR href while
  // client-rendered ones carried the quartet. A post-mount state update is
  // applied. Re-runs on a consent decision, so granting drops the parameter
  // it has just made redundant rather than leaving it there for the visit.
  const [href, setHref] = useState(baseHref);

  useEffect(() => {
    const sync = () => setHref(appendLandingAttributionToHref(baseHref));
    sync();
    window.addEventListener(CONSENT_CHANGE_EVENT, sync);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, sync);
  }, [baseHref]);

  const onClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // Modified clicks open a new tab/window — this document stays put, so
      // no pending state. No parking either: the in-flight POST only dies
      // when *this* page unloads, and a new tab doesn't unload it.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      // The pending buttons drop pointer events, but `aria-disabled` doesn't
      // block keyboard activation — a repeat Enter would fire a second load.
      if (isCartNavigationInFlight) {
        event.preventDefault();
        return;
      }
      // A parked click is released by `endAdd` straight to the cart, past the
      // phone-sticker gate below. Accepted: it only happens mid-add, and the
      // buyer's next trip to the cart is offered it.
      if (parkCartNavigation(href)) {
        event.preventDefault();
        return;
      }
      // The phone-sticker offer (spec #107) takes the click over when there
      // is one; its accept and decline continue to `href`.
      if (interceptCartNavigation(href)) {
        event.preventDefault();
        return;
      }
      beginCartNavigation();
    },
    [
      href,
      parkCartNavigation,
      isCartNavigationInFlight,
      beginCartNavigation,
      interceptCartNavigation,
    ],
  );

  return {
    href,
    onClick,
    isParked: isCartNavigationParked,
    isNavigating: isCartNavigationInFlight,
  };
}
